import { Component, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { Address, countryDefaultValue } from '@models/address/address';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { IndividualData } from '@models/individuals/individual-data';
import { ParsedName } from "@models/individuals/parsed-name";
import { CompanyChangeOfficerType } from '@models/relationships/company-change-officer-type';
import { OfficerAppointmentType } from '@models/relationships/officer-appointment-type';
import { OfficerRelationshipDetails, Relationship } from '@models/relationships/relationship';
import { RelationshipType, RelationshipTypeLabelsPlural } from '@models/relationships/relationship-type.enum';
import { SelectOption } from '@models/ui-models/select-options';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { AutocompleteComponent } from "@shared/controls/autocomplete/autocomplete.component";
import { CheckboxGroupComponent } from "@shared/controls/checkbox-group/checkbox-group.component";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import {
  DinMaskedInputComponent
} from "@shared/controls/masked-input/specific-masked-inputs/din-masked-input/din-masked-input.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { TextareaComponent } from "@shared/controls/textarea/textarea.component";
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";
import { YesNoControlComponent } from "@shared/controls/yes-no-control-component/yes-no-control.component";
import { autocompleteServiceToken } from '@shared/services/autocomplete.service';
import { AuxiliaryApiService } from '@shared/services/auxiliary-api.service';
import { CountriesService } from "@shared/services/countries.service";
import { parseFullName } from "@shared/utils/parse-fullname";
import { setControlDisabled } from '@shared/utils/set-control-disabled';
import { DatepickerHelper } from '@shared/validators/date-picker-form-validators';
import { CompanyChangeOfficer } from '../../../models/company';
import { addressIncludesPoBox, poBoxAddressCustomErrors } from "../../../models/company/company-change-address.model";
import {
  IndividualDataFormGroup,
  IndividualDataFormGroupComponent,
} from "../../../reusable-form-groups/individual-data-form-group/individual-data-form-group.component";
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";
import { BaseStepperFormComponent, StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";

export enum OfficerAppointmentStepsEnum {
  FormDescription = 0,
  AppointOfficer = 1,
}

export function getPositionTypesOptions(): SelectOption[] {
  return [
    { label: 'Director', value: OfficerAppointmentType.Director },
    { label: 'Secretary', value: OfficerAppointmentType.Secretary },
    { label: 'Alternate Director', value: OfficerAppointmentType.AlternativeDirector }
  ];
}

export interface OfficerAppointmentControls {
  changeDate: FormControl<Date | null>;
  isNewOfficeholder: FormControl<boolean | null>;
  appointedOfficerId: FormControl<string | null>;
  type: FormControl<(RelationshipType | OfficerAppointmentType)[] | null>;
  din: FormControl<string | null>;
  alternativeDirectorFor: FormControl<string | null>;
  expiryDate: FormControl<Date | null>;
  termsOfAppointment: FormControl<string | null>;
  newOfficeholder: IndividualDataFormGroup;
}

export type OfficerAppointmentFormGroup = FormGroup<OfficerAppointmentControls>;

@Component({
  selector: 'app-bulk-company-change-officer-appoint',
  standalone: true,
  templateUrl: './company-change-officer-appoint.component.html',
  styleUrl: '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
  providers: [{ provide: autocompleteServiceToken, useClass: AuxiliaryApiService }],
  imports: [
    ReactiveFormsModule,
    StepperFormComponent,
    StepperFormDescriptionComponent,
    SelectComponent,
    TextareaComponent,
    AutocompleteComponent,
    DatePickerComponent,
    YesNoControlComponent,
    DinMaskedInputComponent,
    IndividualDataFormGroupComponent,
    ValidationErrorComponent,
    CheckboxGroupComponent,
    DisclaimerComponent,
  ]
})
export class CompanyChangeOfficerAppointComponent extends BaseStepperFormComponent<OfficerAppointmentStepsEnum, CompanyChangeOfficer> implements OnInit {
  @Input() officers: Record<string, Relationship[]> = {};
  @Input() officeholders: Relationship[] = [];
  @Input() officersSelectOptions: SelectOption[] = [];
  @Input() allDirectorsOptions: SelectOption[] = [];
  @Input() moreThenOneDirectorExist = false;

  override readonly StepsEnum = OfficerAppointmentStepsEnum;
  readonly OfficerAppointmentType = OfficerAppointmentType;
  readonly addressesCustomErrors = poBoxAddressCustomErrors;

  positionTypesOptions = getPositionTypesOptions();
  disabledPositionTypesOptions: Partial<Record<RelationshipType, boolean>> = {};

  form: OfficerAppointmentFormGroup = new FormGroup<OfficerAppointmentControls>({
    changeDate: new FormControl<Date | null>(null),
    isNewOfficeholder: new FormControl<boolean | null>(null, [Validators.required]),
    appointedOfficerId: new FormControl<string | null>(null, [Validators.required]),
    type: new FormControl<RelationshipType[]>([], { nonNullable: true }),
    din: new FormControl<string | null>(null),
    alternativeDirectorFor: new FormControl<string | null>(null, [Validators.required]),
    expiryDate: new FormControl<Date | null>(null),
    termsOfAppointment: new FormControl<string | null>(null, [Validators.required]),
    newOfficeholder: IndividualDataFormGroupComponent.defineForm()
  });
  override stepperForm = new FormGroup({
    [OfficerAppointmentStepsEnum.FormDescription]: new FormGroup({}),
    [OfficerAppointmentStepsEnum.AppointOfficer]: this.form,
  });

  isNewOfficeholderSignal = signal<boolean | null>(null);
  allowedDirectorsSignal: WritableSignal<SelectOption[]> = signal([]);

  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;

  constructor() {
    super();
    this.setupSteps(OfficerAppointmentStepsEnum);
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getDateOfEstablishmentMinDate(this.companyChangeData.dateOfEstablishment);
    this.maxDate = DatepickerHelper.getNextNYearsStruct(10);
    this.formModel.actionType = CompanyChangeOfficerType.Appointment;
    this.form.controls.changeDate.setValue(DatepickerHelper.getToday());

    this.listenIsNewOfficerChanges();
    this.listenAppointedOfficerChanges();
    this.listenChangeDateChange(this.form.controls.changeDate, getForm484LateFeeNotification);

    this.addOfficeholderAddressPoBoxValidator();
    this.setOfficerTypesValidator();

    this.setupChange();
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as CompanyChangeOfficer);
  }

  override setupChange(change: CompanyChangeOfficer = this.formModel): void {
    if (!change?.offices[0]?.individualDataOverride) return;

    const appointedOfficerId = this.officersSelectOptions
      .find(of => of.label.startsWith(change.offices[0]!.individualDataOverride!.fullName))?.value as string ?? null;
    this.form.controls.appointedOfficerId.setValue(appointedOfficerId ?? null);

    const allCompanyRelationship = Object.values(this.officers).flat();
    const isNewOfficeholder = !appointedOfficerId;
    const din = change.offices
      .find(relationship =>
        (relationship.type === RelationshipType.Director
          || relationship.type === RelationshipType.AlternativeDirector)
        && relationship.individualDataOverride?.din)?.individualDataOverride?.din ?? null;
    const selectedAlternativeDirectorDetails = (change.offices.find(relationship => relationship.type === RelationshipType.AlternativeDirector)
      ?.details as OfficerRelationshipDetails | undefined) ?? null;
    const alternativeDirectorForId = (selectedAlternativeDirectorDetails)?.alternativeDirectorFor ?? null;
    const alternativeDirectorFor = allCompanyRelationship.find(relationship => relationship.relationshipId === alternativeDirectorForId) ?? null;
    const alternativeDirectorForKey = alternativeDirectorFor?.key;

    this.form.patchValue({
      ...change,
      isNewOfficeholder,
      din,
      type: (change.offices ?? []).map((officer) => officer.type).filter((type) => this.positionTypesOptions.find((allowedType) => allowedType.value === type)),
      alternativeDirectorFor: alternativeDirectorForKey,
      termsOfAppointment: selectedAlternativeDirectorDetails?.termsOfAppointment ?? null,
      expiryDate: selectedAlternativeDirectorDetails?.expiryDate ?? null,
    });

    if (change?.offices[0]?.individualDataOverride) {
      const officeholder = new IndividualData(change.offices[0]?.individualDataOverride);
      this.form.controls.newOfficeholder.patchValue({
        ...officeholder,
        formerFullName: officeholder.formerName.fullNameWithMiddle,
        address: {
          ...officeholder.address,
          country: officeholder.address.country || countryDefaultValue
        }
      });
    }

    if (!isNewOfficeholder) {
      this.updateDisabledPositionTypesOptions();
    }
  }

  override buildDocument(): Document | null {
    const formValue = this.form.value;
    const change = new CompanyChangeOfficer({
      changeDate: formValue.changeDate!,
      description: (formValue.newOfficeholder?.fullName ?? '') + ' officer appointment',
    });
    if (formValue.isNewOfficeholder === true) {
      // fill changes for new officer
      const names = parseFullName(formValue.newOfficeholder?.fullName ?? '');
      const formerName = parseFullName(formValue.newOfficeholder?.formerFullName ?? '') as ParsedName;
      const birthCountry = CountriesService.getAustralianCodeByStateName(formValue.newOfficeholder?.birthCountry ?? '')
        || (formValue.newOfficeholder?.birthCountry ?? '');

      const newOfficerIndividualData = new IndividualData({
        ...names,
        formerName,
        dob: formValue.newOfficeholder!.dob!,
        address: new Address({
          ...formValue.newOfficeholder?.address,
          normalizedFullAddress: formValue.newOfficeholder?.address?.normalizedFullAddress,
        } as Address),
        birthCity: formValue.newOfficeholder?.birthCity ?? '',
        birthCountry,
      });

      change.offices = this.form.controls.type.value!
        .map((type) => {
          const isAlternativeDirector = type == RelationshipType.AlternativeDirector;

          return new Relationship({
            start: formValue.changeDate!,
            entityId: this.companyChangeData.entityId,
            individualDataOverride: newOfficerIndividualData,
            type: type as RelationshipType,
            details: new OfficerRelationshipDetails({
              isAlternativeDirector,
              alternativeDirectorFor: this.selectedAlternativeDirectorForRelationshipId,
              termsOfAppointment: this.form.controls.termsOfAppointment.value ?? undefined,
              expiryDate: DatepickerHelper.buildDateString(this.form.controls.expiryDate.value!),
            })
          });
        });
    } else if (formValue.isNewOfficeholder === false) {
      // fill changes for existing officer
      const existingOfficerRelationshipData = this.officers[formValue.appointedOfficerId!][0];
      const existingOfficerRelationships = this.officers[formValue.appointedOfficerId!]
        .filter((existingOfficerRelationship) => this.form.controls.type.value?.includes(existingOfficerRelationship.type));
      const existingOfficerRelationshipsTypes = existingOfficerRelationships.map((relationship) => relationship.type);

      change.offices = this.form.controls.type.value!
        .filter((selectedOfficeholderType) => !existingOfficerRelationshipsTypes.includes(selectedOfficeholderType as RelationshipType))
        .map((type) => {
          return new Relationship({
            ...existingOfficerRelationshipData,
            start: formValue.changeDate!,
            end: undefined,
            type: type as RelationshipType,
          });
        });
    }

    change.offices = change.offices.map((relationship) => {
      if (formValue.type && (relationship.type as any as OfficerAppointmentType) !== OfficerAppointmentType.Secretary) {
        relationship.individualDataOverride!.din = formValue?.din ?? '';
      }

      if ((relationship.type as any as OfficerAppointmentType) === OfficerAppointmentType.AlternativeDirector && formValue.alternativeDirectorFor) {
        relationship.details = new OfficerRelationshipDetails({
          isAlternativeDirector: true,
          alternativeDirectorFor: this.selectedAlternativeDirectorForRelationshipId,
          termsOfAppointment: this.form.controls.termsOfAppointment.value ?? undefined,
          expiryDate: DatepickerHelper.buildDateString(this.form.controls.expiryDate.value!)
        });
      }

      return relationship;
    });

    return new Document({
      changes: [change],
      entityId: this.companyChangeData.entityId,
      type: 'c:484',
      documentId: this.companyChangeData?.documentId,
    });
  }

  private listenIsNewOfficerChanges(): void {
    this.form.controls.isNewOfficeholder.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isNewOfficeholder) => {
        setControlDisabled(this.form.controls.appointedOfficerId, isNewOfficeholder === true);
        setControlDisabled(this.form.controls.din, isNewOfficeholder === true);
        setControlDisabled(this.form.controls.alternativeDirectorFor, isNewOfficeholder === true);
        setControlDisabled(this.form.controls.newOfficeholder, isNewOfficeholder !== true);


        if (isNewOfficeholder) {
          this.allowedDirectorsSignal.set(this.allDirectorsOptions.filter(director => director.value !== this.form.value.appointedOfficerId));
          this.form.controls.type.setValue([]);
          this.positionTypesOptions = this.positionTypesOptions.map((option) => ({ ...option, disabled: false }));
        } else if (this.form.controls.appointedOfficerId.value !== null) {
          this.allowedDirectorsSignal.set([]);
          const selectedOfficer = this.officers[this.form.controls.appointedOfficerId.value];
          const selectedOfficerPositions = selectedOfficer.map((officer) => officer.type);
          this.form.controls.type.setValue(selectedOfficerPositions);
          this.form.controls.appointedOfficerId.setValue(this.form.value.appointedOfficerId ?? null);
        }

        this.isNewOfficeholderSignal.set(isNewOfficeholder);
      });
  }

  onIndividualSelected($event: IndividualData) {
    this.form.controls.din.setValue($event.din);
  }

  onPositionChanged(values: unknown[]): void {
    const types = (values ?? []) as RelationshipType[];
    const typesIncludeDirector = types.includes(RelationshipType.Director) && this.isDirectorSelectedAndEnabled;
    const typesIncludeAltDirector = types.includes(RelationshipType.AlternativeDirector) && this.isAltDirectorSelectedAndEnabled;

    setControlDisabled(this.form.controls.alternativeDirectorFor, !typesIncludeAltDirector);
    setControlDisabled(this.form.controls.termsOfAppointment, !typesIncludeAltDirector);
    setControlDisabled(this.form.controls.din, !!types?.length && !(typesIncludeDirector || typesIncludeAltDirector));

    if (!types.includes(RelationshipType.AlternativeDirector)) {
      this.form.controls.alternativeDirectorFor.setValue(null);
    }

    this.form.updateValueAndValidity();
  }

  listenAppointedOfficerChanges(): void {
    this.form.controls.appointedOfficerId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((appointedOfficerId) => {
          if (appointedOfficerId) {
            const selectedOfficerRelationships = this.officers[appointedOfficerId];
            const selectedOfficerTypes = selectedOfficerRelationships.map((officer) => officer.type);

            this.form.controls.type.setValue(selectedOfficerTypes);
            this.positionTypesOptions = getPositionTypesOptions().map(({ label, value }) => ({
              label,
              value,
              disabled: selectedOfficerTypes.includes(value as RelationshipType)
            }));
            this.updateDisabledPositionTypesOptions();
            this.allowedDirectorsSignal.set(this.allDirectorsOptions.filter((director) => director.value !== appointedOfficerId));

            if (this.form.controls.alternativeDirectorFor.value && this.form.controls.alternativeDirectorFor.value === appointedOfficerId) {
              this.form.controls.alternativeDirectorFor.setValue(null);
            }
            return;
          }

          this.positionTypesOptions = getPositionTypesOptions();
          this.updateDisabledPositionTypesOptions();
        }
      );
  }

  private addOfficeholderAddressPoBoxValidator(): void {
    const getOfficeholdersByType = (officeholders: Relationship[], type: RelationshipType) => officeholders.filter((office) => office.type === type);
    const officersIncludeAtLeastOneNotPoBoxAuAddress = (officeholders: Relationship[]) => officeholders
      .some((officeholder) => {
        return officeholder.individualDataOverride
          && !addressIncludesPoBox(officeholder.individualDataOverride.address.normalizedFullAddress)
          && CountriesService.isAustralianStateOrAustralia(officeholder.individualDataOverride.address.country);
      });

    const officeholderAddressPoBoxValidator = (control: AbstractControl): ValidationErrors | null => {
      const form = control as OfficerAppointmentFormGroup;
      const isNewOfficeholder = form.controls.isNewOfficeholder.value;
      const types = form.controls.type.value ?? [];

      if (!isNewOfficeholder || !types.length) {
        return null;
      }

      const isPoBoxType = addressIncludesPoBox(form.controls.newOfficeholder.controls.address.controls.normalizedFullAddress.value ?? '');

      if (!isPoBoxType) {
        return null;
      }

      const officeholdersWithSameType = types.flatMap((type) => getOfficeholdersByType(this.officeholders, type as RelationshipType));

      return officersIncludeAtLeastOneNotPoBoxAuAddress(officeholdersWithSameType)
        ? null
        : { poBoxAddressOfficeholder: types.map((type) => RelationshipTypeLabelsPlural[type]).join(', ') };
    };

    this.form.addValidators(officeholderAddressPoBoxValidator);
  }

  private setOfficerTypesValidator(): void {
    const minOneSelectedValidator = (control: AbstractControl): ValidationErrors | null => {
      const isNewOfficeholderControl = (control.parent as OfficerAppointmentFormGroup)?.controls.isNewOfficeholder;
      if (isNewOfficeholderControl.value) return null;

      const selectedTypes = (control.value ?? []) as RelationshipType[];
      const selectedEnabledTypes = selectedTypes.filter((type) => !this.disabledPositionTypesOptions[type]);

      return selectedEnabledTypes.length > 0
        ? null
        : { required: true };
    };

    this.form.controls.type.addValidators(minOneSelectedValidator.bind(this));
    CheckboxGroupComponent.addRequiredAtLeastOneValidator(this.form.controls.type);
  }

  private updateDisabledPositionTypesOptions(): void {
    this.positionTypesOptions.forEach((positionOption) => {
      this.disabledPositionTypesOptions[positionOption.value as RelationshipType] = positionOption.disabled;
    });
  }

  get minExpiryDate(): NgbDateStruct {
    return DatepickerHelper.getStructFromDateOrNull(this.form.controls.changeDate.value) ?? this.minDate;
  }

  get fullNameForDinCheck(): string | null {
    if (this.form.controls.isNewOfficeholder.value) {
      if (this.form.controls.newOfficeholder.controls.fullName.valid && this.form.controls.newOfficeholder.controls.fullName.value?.length) {
        const parsedName = parseFullName(this.form.controls.newOfficeholder.controls.fullName.value);
        return [parsedName.firstName, parsedName.lastName].join(' ').trim();
      }

      return null;
    }

    if (!this.form.controls.appointedOfficerId.value)
      return null;

    const selectedOfficer = this.officers[this.form.controls.appointedOfficerId.value][0];
    return selectedOfficer.individualDataOverride?.fullName ?? null;
  }

  get selectedAlternativeDirectorForRelationshipId(): string | undefined {
    return this.form.controls.alternativeDirectorFor.value
      ? this.officers[this.form.controls.alternativeDirectorFor.value].find((officer) => officer.type == RelationshipType.Director)?.relationshipId
      : undefined;
  }

  get isAltDirectorSelectedAndEnabled(): boolean {
    return Boolean(this.form.controls.type.value
      && this.form.controls.type.value.includes(OfficerAppointmentType.AlternativeDirector)
      && !this.disabledPositionTypesOptions[OfficerAppointmentType.AlternativeDirector]);
  }

  get isDirectorSelectedAndEnabled(): boolean {
    return Boolean(this.form.controls.type.value
      && this.form.controls.type.value.includes(OfficerAppointmentType.Director)
      && !this.disabledPositionTypesOptions[OfficerAppointmentType.Director]);
  }
}
